/** @format */

import {useTheme} from '@mui/material/styles';
import {Trans, useTranslation} from 'react-i18next';
import SensorsIcon from '@mui/icons-material/Sensors';

import {divideBySpaces} from 'helpers/numberHelpers';
import {sensorName} from 'helpers/sensor_names';
import {getKpiUnit} from 'helpers/kpiHelpers';

import SimpleAreaChart from 'components/charts/SimpleAreaChart/SimpleAreaChart';

import StarIcon from './star-icon.svg?react';

import {CampaignCardData} from '../..';
import {MAX_VISIBLE_SENSORS} from '../../constants';
import {getSensorIcon} from './helpers/getSensorIcon';
import {
  StyledCard,
  HeaderRow,
  CampaignName,
  SpentValue,
  ChartContainer,
  KpiContainer,
  KpiValue,
  DeviationValue,
  SensorsList,
  SensorItem,
  SensorName,
  SensorCount,
  NoSensorsErrorsSlug,
} from './styles';

interface CampaignCardProps {
  data: CampaignCardData;
}

export const CampaignCard = ({data}: CampaignCardProps) => {
  const theme = useTheme();
  const {t} = useTranslation();

  const chartColor = data.isGood
    ? theme.palette.semantic_dark?.good.main || '#419466'
    : theme.palette.semantic_dark?.bad.main || '#C06868';

  const kpiUnit = getKpiUnit(data.kpiName);

  const remainingSensorsCount = data.sensors.length - MAX_VISIBLE_SENSORS + 1;
  const shouldShowCounter = data.sensors.length > MAX_VISIBLE_SENSORS;
  const sensorsToShow = shouldShowCounter
    ? data.sensors.slice(0, MAX_VISIBLE_SENSORS - 1)
    : data.sensors;

  return (
    <StyledCard isGood={data.isGood}>
      <HeaderRow>
        <CampaignName>{data.campaignName}</CampaignName>
        <KpiContainer>
          <KpiValue>
            {data.kpiDisplayName || data.kpiName} {divideBySpaces(data.kpiFact)} {kpiUnit}
          </KpiValue>
          {data.kpiPlan !== undefined && (
            <>
              <DeviationValue valueColor={chartColor}>
                {data.kpiDeviation > 0 ? '+' : ''}
                {divideBySpaces(data.kpiDeviation)} {kpiUnit} <Trans>от плана</Trans>
              </DeviationValue>
              <DeviationValue valueColor={chartColor}>
                {Math.round((data.kpiFact / data.kpiPlan) * 100)}% <Trans>плана</Trans>
              </DeviationValue>
            </>
          )}
        </KpiContainer>
      </HeaderRow>

      <ChartContainer>
        <SpentValue>{divideBySpaces(data.spent)} ₽</SpentValue>
        <SimpleAreaChart
          data={data.spentChartData}
          height={80}
          strokeColor={chartColor}
          fillColor={chartColor}
          gradientId={`gradient-${data.campaignId}`}
        />
      </ChartContainer>

      {data.hasErrors ? (
        <SensorsList>
          {sensorsToShow.map(sensor => (
            <SensorItem key={sensor.id}>
              {getSensorIcon(theme, sensor.severity)}
              <SensorName>{sensorName(sensor.name)}</SensorName>
              <SensorCount>{sensor.count}</SensorCount>
            </SensorItem>
          ))}
          {shouldShowCounter && remainingSensorsCount > 0 && (
            <SensorItem>
              <SensorsIcon
                sx={{
                  fontSize: 16,
                  color: theme.palette.semantic_dark?.surface.onSurfaceMuted,
                }}
              />
              <SensorName>Ещё {t('plurals.sensors', {count: remainingSensorsCount})}</SensorName>
            </SensorItem>
          )}
        </SensorsList>
      ) : (
        <NoSensorsErrorsSlug>
          <StarIcon />
          Кампания
          <br />
          без ошибок
        </NoSensorsErrorsSlug>
      )}
    </StyledCard>
  );
};
